import React, {useEffect} from 'react';
import wrap from 'wraptile';
import {
    ActionFunction1,
    Action,
} from 'redux-actions';
import {curry} from 'ramda';

import {TimedText} from '@pluto/rest/lib/qc-types';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {TimelineRow} from '../TimelineTypes';
import SubtitleTrack from './SubtitleTrack/SubtitleTrackConnected';
import SubtitleTracks from './SubtitleTracks';
import {EnabledSubtitles} from './Redux/SubtitleTracksReducer';
import {SetSubtitle} from './Redux/SubtitleTracksActions';

export interface SubtitleTracksContainerProps {
    item: Item;
    subtitleTracks: TimelineRow[];
    enabledTracks: EnabledSubtitles;
    setSubtitle: SetSubtitle;
    resetSubtitles: ActionFunction1<void, Action<void>>;
}

const mapSubtitleTracks = curry((props: SubtitleTracksContainerProps, subtitleTrackData: TimelineRow) => {
    const {
        enabledTracks,
        item,
    } = props;
    const {
        frameRate,
        dropFrame,
    } = item.video;
    const {identifier} = subtitleTrackData;
    const enabled = Boolean(enabledTracks[identifier]);
    
    return(
        <SubtitleTrack
            key={subtitleTrackData.identifier}
            subtitleTrackData={subtitleTrackData}
            enabled={enabled}
            frameRate={frameRate}
            dropFrame={dropFrame}
        />
    );
});

export const getSubtitleTracks = (props: SubtitleTracksContainerProps) => {
    const {subtitleTracks} = props;
    
    if (!subtitleTracks) {
        return null;
    }
    
    const handleMap = mapSubtitleTracks(props);
    
    return subtitleTracks.map(handleMap);
};

export const addSubtitle = wrap((props: SubtitleTracksContainerProps, timedText: TimedText) => {
    const {
        setSubtitle,
        item,
    } = props;
    const {video} = item;
    
    setSubtitle(timedText, video);
});

export const onAddSubtitles = wrap((props: SubtitleTracksContainerProps) => {
    const {item} = props;
    const {timedTexts} = item;
    const handleAddSubtitle = addSubtitle(props);
    
    timedTexts.forEach(handleAddSubtitle);
});

export const handleMount = wrap((props: SubtitleTracksContainerProps, addSubtitles: () => void) => {
    const {resetSubtitles} = props;
    
    addSubtitles();
    
    return resetSubtitles;
});

export default function SubtitleTracksContainer(props: SubtitleTracksContainerProps) {
    const subtitleTracks = getSubtitleTracks(props);
    const addSubtitles = onAddSubtitles(props);
    const onMount = handleMount(props, addSubtitles);
    useEffect(onMount, []);
    
    return (
        <SubtitleTracks subtitleTracks={subtitleTracks} />
    );
}
